import { NoSecondTryPreview } from './NoSecondTryPreview.js';
import { isNoSecondTry } from '../lib/workItems.js';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { DeicticSafetyFlag } from './DeicticSafetyFlag.js';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ApiRequestError,
  approveItem,
  approveItemWithEdit,
  cancelItem,
  commentItem,
  fetchAnnotations,
  fetchEntity,
  fetchEntityDependents,
  fetchGroups,
  fetchWorkItem,
  rateItem,
  searchSourceDocument,
  sourceDocumentHref,
  type EntityRef,
  type GroupNode,
  type SourceSearchResult,
} from '../api/client.js';
import { patchToEntity } from '../lib/draftCapture.js';
import {
  COMMENT_STATUS_STYLES,
  describeCommentDelivery,
  type CommentStatus,
} from '../lib/commentDelivery.js';
import { useDraftCapture } from '../lib/useDraftCapture.js';
import { useItemOpenedSignal } from '../activity/useItemOpenedSignal.js';
import { useUnsavedWorkProbe } from '../lib/unsavedWork.js';
import { describeSources } from '../lib/sources.js';
import { deriveReviewerBrief } from '../lib/reviewerBrief.js';
import { deriveLessonContext, prerequisiteEntityIds, type LessonContextNode } from '../lib/lessonContext.js';
import {
  BUTTON_INTERACTION_STYLES,
  LINK_INTERACTION_STYLES,
  PRIMARY_ACTION_TEXT_STYLES,
} from '../lib/actionStyles.js';
import { hashPatch } from '../lib/patchHash.js';
import {
  enforceRatingsBeforeApprove,
  getRatingDimensions,
  resolveStoredRatings,
  type RatingDimension,
} from '../lib/ratingConfig.js';
import {
  canConfirmRejection,
  findRejectionReason,
  getRejectionReasons,
  rejectionItemKind,
  rejectionNoteRequired,
} from '../lib/rejectionReasons.js';
import {
  buildCancelNotes,
  effectiveWorkItemPatch,
  isLessonDraft,
  isLessonFill,
  isPlacedLesson,
  queueBackPath,
  statusBadgeClass,
} from '../lib/workItems.js';
import {
  courseMismatchMessage,
  detectCourseMismatch,
  detectUndeclaredCourse,
  undeclaredCourseMessage,
} from '../lib/courseMismatch.js';
import { readCourseParam } from '../lib/courseParam.js';
import type { AnnotationView, RatingSnapshot, WorkItemSummary } from '../types/workflow.js';
import { LessonPlacementBanner } from './LessonPlacementBanner.js';
import { LessonFillBanner } from './LessonFillBanner.js';
import { PlayerPreview } from './PlayerPreview.js';
import { ReviewColumns } from './ReviewColumns.js';
import { AnnotationDrawer, annotationCount } from './AnnotationDrawer.js';

const DRAFT_ENTITY_ID = 999999901;
const DRAFT_CAPTURE_NAMESPACE = '/author-draft-capture';

type DraftChangeOrigin = 'load' | 'drawer' | 'player';
type RatingBaseline = {
  ratings: Record<string, number>;
  trimmedNote: string;
  ratedPatchHash: string;
};

/**
 * Route entry point — see `KeyedPracticeItemReview` for the reasoning. This surface carries even more
 * item-scoped state (ratings, rejection form, comment draft and delivery status, edit drawer, display
 * modes), so remounting on `workItemId` rather than enumerating resets matters more here, not less.
 */
export function KeyedMicroLessonReview({ viewerId }: { viewerId?: string | undefined }) {
  const { workItemId = '' } = useParams();
  return <MicroLessonReview key={workItemId} viewerId={viewerId} />;
}

export function MicroLessonReview({ viewerId }: { viewerId?: string | undefined }) {
  const { workItemId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const backPath = queueBackPath(searchParams);
  const [item, setItem] = useState<WorkItemSummary | null>(null);
  const [lessonPreviewReadyId, setLessonPreviewReadyId] = useState<string | null>(null);
  const previewWorkItemId = item && isNoSecondTry(item) ? item.workItemId : workItemId;
  const onLessonPreviewReady = useCallback((ready: boolean) => {
    setLessonPreviewReadyId(ready ? previewWorkItemId : null);
  }, [previewWorkItemId]);
  const lessonPreviewBlocked = !!item && (isPlacedLesson(item) || isNoSecondTry(item)) && lessonPreviewReadyId !== workItemId;
  const [originalPatch, setOriginalPatch] = useState<Record<string, unknown> | null>(null);
  const [draftPatch, setDraftPatch] = useState<Record<string, unknown> | null>(null);
  const [previewPatch, setPreviewPatch] = useState<Record<string, unknown> | null>(null);
  const draftPatchRef = useRef<Record<string, unknown> | null>(null);
  const draftChangeOriginRef = useRef<DraftChangeOrigin | null>(null);
  const [selectedEvidence, setSelectedEvidence] = useState(0);
  const [sourceSearchQuery, setSourceSearchQuery] = useState('');
  const [sourceSearchResult, setSourceSearchResult] = useState<SourceSearchResult | null>(null);
  const [sourceSearchBusy, setSourceSearchBusy] = useState(false);
  const [sourceSearchError, setSourceSearchError] = useState('');
  const [sourceResultIndex, setSourceResultIndex] = useState(-1);
  const [sourcePage, setSourcePage] = useState<{ path: string; page: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'edit' | 'annotate'>('edit');
  const [annotations, setAnnotations] = useState<AnnotationView[]>([]);
  const [previewMode, setPreviewMode] = useState<'editable' | 'vanilla'>('editable');
  const [playerFullscreen, setPlayerFullscreen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [comment, setComment] = useState('');
  const [commentStatus, setCommentStatus] = useState<CommentStatus | null>(null);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [ratingNote, setRatingNote] = useState('');
  const [ratingBusy, setRatingBusy] = useState(false);
  const [ratingStatus, setRatingStatus] = useState('');
  const [ratingBaseline, setRatingBaseline] = useState<RatingBaseline | null>(null);
  const [ratingDetailsOpen, setRatingDetailsOpen] = useState(false);
  const ratingDetailsId = useId();
  const [contextGroups, setContextGroups] = useState<GroupNode[]>([]);
  const [contextDependents, setContextDependents] = useState<EntityRef[]>([]);
  const [contextPrereqTitles, setContextPrereqTitles] = useState<ReadonlyMap<string, string>>(new Map());
  /**
   * The course this screen was OPENED for. It is what the decision declares to the server, and it
   * must never be `item.courseKey` — that would compare a value with itself and pass always.
   *
   * Read at the top rather than beside the guard below, because the activity signal needs it too.
   */
  const displayedCourseKey = readCourseParam(searchParams);
  /**
   * "This item was opened and read" is recorded ONLY when the screen actually shows the item.
   *
   * A refusal panel is not a reading. Both hard stops below — a link naming a different course, and
   * a link naming none — return before any item content renders, and this signal fired before
   * either of them: a reviewer who saw only the refusal was recorded as having opened the lesson.
   * That is not cosmetic. These rows feed the per-person activity report and the presence
   * arithmetic that grades a worked day, so a false positive here inflates a number somebody is
   * measured on. Following the declaration link flips the key from `undefined` to the id and the
   * signal is emitted then, which is the moment the item is genuinely on screen.
   */
  const itemIsActuallyShown = Boolean(item && displayedCourseKey && displayedCourseKey === item.courseKey);
  useItemOpenedSignal(itemIsActuallyShown ? workItemId : undefined);

  useEffect(() => {
    if (!workItemId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setNotFound(false);
    void fetchWorkItem(workItemId)
      .then((loadedItem) => {
        if (cancelled) return;
        const patch = effectiveWorkItemPatch(loadedItem);
        draftPatchRef.current = patch;
        draftChangeOriginRef.current = 'load';
        setItem(loadedItem);
        setOriginalPatch(patch);
        setDraftPatch(patch);
        setPreviewPatch(patch);
        setSelectedEvidence(0);
        setRatings({ ...(loadedItem.latestRatings?.ratings ?? {}) });
        setRatingNote(loadedItem.latestRatings?.note ?? '');
        setRatingBaseline(loadedItem.latestRatings ? ratingBaselineFromSnapshot(loadedItem.latestRatings) : null);
        setRatingStatus(loadedItem.latestRatings
          ? `Rated by ${loadedItem.latestRatings.ratedBy} · ${formatRatingAge(loadedItem.latestRatings.ratedAt)}`
          : '');
        setRatingDetailsOpen(false);
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        if (reason instanceof ApiRequestError && reason.status === 404) {
          setNotFound(true);
          return;
        }
        setError(reason instanceof Error ? reason.message : 'Unable to load this lesson draft.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId]);

  useEffect(() => {
    if (!workItemId) return;
    let cancelled = false;
    void fetchAnnotations(workItemId)
      .then((result) => { if (!cancelled) setAnnotations(result.annotations); })
      .catch(() => { if (!cancelled) setAnnotations([]); });
    return () => { cancelled = true; };
  }, [workItemId]);

  useEffect(() => {
    if (!item) {
      setContextGroups([]);
      setContextDependents([]);
      setContextPrereqTitles(new Map());
      return;
    }

    let cancelled = false;
    // The relationships shown in the strip (prerequisites, parent group, dependents)
    // are not reviewer-editable, so derive them from the item once — not per keystroke.
    const patch = effectiveWorkItemPatch(item);
    const prereqIds = prerequisiteEntityIds(patch);
    // Dependents exist only for edits of an existing entity; a create draft has no
    // entity id yet, and querying with any other id would cross namespaces.
    const targetEntityId = item.targetType === 'entity' && item.targetId != null ? Number(item.targetId) : null;
    setContextGroups([]);
    setContextDependents([]);
    setContextPrereqTitles(new Map());
    void Promise.allSettled([
      fetchGroups(item.courseKey),
      targetEntityId != null && Number.isFinite(targetEntityId)
        ? fetchEntityDependents(item.courseKey, targetEntityId)
        : Promise.resolve([] as EntityRef[]),
      Promise.allSettled(prereqIds.map((id) => fetchEntity(item.courseKey, id))),
    ]).then(([groupsResult, dependentsResult, prereqResult]) => {
      if (cancelled) {
        return;
      }
      setContextGroups(groupsResult.status === 'fulfilled' ? groupsResult.value : []);
      setContextDependents(dependentsResult.status === 'fulfilled' ? dependentsResult.value : []);
      const titles = new Map<string, string>();
      if (prereqResult.status === 'fulfilled') {
        for (const settled of prereqResult.value) {
          if (settled.status === 'fulfilled' && typeof settled.value?.title === 'string' && settled.value.title.trim()) {
            titles.set(String(settled.value.id), settled.value.title.trim());
          }
        }
      }
      setContextPrereqTitles(titles);
    });

    return () => {
      cancelled = true;
    };
  }, [item]);

  useDraftCapture({
    captureNamespace: DRAFT_CAPTURE_NAMESPACE,
    draftEntityId: DRAFT_ENTITY_ID,
    draftPatchRef,
    emptyDraftError: 'No lesson draft is loaded.',
    onPatchChange: (patch) => {
      draftChangeOriginRef.current = 'player';
      setDraftPatch(patch);
    },
  });

  useEffect(() => {
    if (!draftPatch) return;
    const origin = draftChangeOriginRef.current;
    draftChangeOriginRef.current = null;
    // Loading already assigns previewPatch; a delayed clone would rebuild the untouched player.
    if (origin === 'player' || origin === 'load') return;
    const timer = window.setTimeout(() => {
      setPreviewPatch({ ...draftPatch });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [draftPatch]);

  useEffect(() => {
    if (!playerFullscreen && !ratingDetailsOpen) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setPlayerFullscreen(false);
      setRatingDetailsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [playerFullscreen, ratingDetailsOpen]);

  const evidence = useMemo(() => describeSources(item?.sourceRefs ?? []), [item]);
  const reviewerBrief = useMemo(() => (item ? isNoSecondTry(item) ? {
    headline: 'One attempt review', learnerOutcome: item.review_summary || item.rationale,
    lessonShape: 'Complete before and after content', estimatedMinutes: null, containsMarkup: false, markupNotices: [],
  } : deriveReviewerBrief(item) : null), [item]);
  const lessonContext = useMemo(
    () => deriveLessonContext(draftPatch ?? {}, contextGroups, contextDependents, contextPrereqTitles, item),
    [contextDependents, contextGroups, contextPrereqTitles, draftPatch, item],
  );
  const activeSource = evidence[selectedEvidence];
  const activeSourceHref = activeSource
    ? sourceDocumentHref(
        activeSource.path,
        sourcePage?.path === activeSource.path ? sourcePage.page : activeSource.filePage,
      )
    : '';

  useEffect(() => {
    setSourceSearchQuery('');
    setSourceSearchResult(null);
    setSourceSearchBusy(false);
    setSourceSearchError('');
    setSourceResultIndex(-1);
    setSourcePage(null);
  }, [activeSource?.path]);

  useEffect(() => {
    const query = sourceSearchQuery.trim();
    if (!activeSource || query.length < 2 || query.length > 200) {
      setSourceSearchResult(null);
      setSourceSearchBusy(false);
      setSourceSearchError('');
      setSourceResultIndex(-1);
      return;
    }

    const controller = new AbortController();
    setSourceSearchBusy(true);
    setSourceSearchResult(null);
    setSourceSearchError('');
    setSourceResultIndex(-1);
    const timer = window.setTimeout(() => {
      void searchSourceDocument(activeSource.path, query, controller.signal)
        .then((result) => {
          if (!controller.signal.aborted) setSourceSearchResult(result);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setSourceSearchResult(null);
          setSourceSearchError('Search unavailable for this document.');
        })
        .finally(() => {
          if (!controller.signal.aborted) setSourceSearchBusy(false);
        });
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [activeSource, sourceSearchQuery]);
  const entity = useMemo(
    () => (previewPatch ? patchToEntity(previewPatch, DRAFT_ENTITY_ID) : null),
    [previewPatch],
  );
  const changed = Boolean(originalPatch && draftPatch && JSON.stringify(originalPatch) !== JSON.stringify(draftPatch));
  const canApprove = item?.status === 'pending' || item?.status === 'cancelled';
  const canCancel = item?.status === 'pending' || item?.status === 'approved' || item?.status === 'approved_with_edit';
  // Per-kind rejection vocabulary (§4.1) — resolved from the item, not hardcoded to 'lesson', so
  // one helper decides the vocabulary for every review surface.
  const rejectionReasons = useMemo(
    () => (item ? getRejectionReasons(item.courseKey, rejectionItemKind(item)) : []),
    [item],
  );
  const rejectionNoteMandatory = rejectionNoteRequired(rejectionReasons, cancelReason);
  const ratingDimensions = useMemo(
    () => (item ? getRatingDimensions(item.courseKey, 'lesson') : []),
    [item],
  );
  const configuredRatingKeys = useMemo(
    () => new Set(ratingDimensions.map((dimension) => dimension.key)),
    [ratingDimensions],
  );
  const storedRatingEntries = useMemo(
    () => (item?.latestRatings ? resolveStoredRatings(item.courseKey, 'lesson', item.latestRatings.ratings) : []),
    [item],
  );
  const historicalRatingEntries = storedRatingEntries.filter((entry) => !configuredRatingKeys.has(entry.key));
  const allConfiguredDimensionsRated = ratingDimensions.every((dimension) => ratings[dimension.key] !== undefined);
  const approvalBlockedByRatings = Boolean(
    item && enforceRatingsBeforeApprove(item.courseKey) && !allConfiguredDimensionsRated,
  );
  const approvalBlockedTitle = approvalBlockedByRatings
    ? 'Rate all configured dimensions before approving.'
    : undefined;
  const ratingDirty = !ratingBaseline ||
    !ratingsEqual(ratings, ratingBaseline.ratings) ||
    ratingNote.trim() !== ratingBaseline.trimmedNote;
  const ratingSaved = Boolean(ratingBaseline && !ratingDirty);
  /**
   * Declares to the stale-version reload what would be lost by reloading right now.
   *
   * Placed here rather than beside the other derivations because it needs `ratingDirty`, and
   * **`ratingDirty` alone is the wrong predicate**: it is `!ratingBaseline || …`, so an item nobody
   * has ever rated reads as permanently dirty and would block the automatic reload on every lesson
   * screen forever. What matters is whether the reviewer has entered something that is not saved —
   * measured against the baseline where there is one, and against emptiness where there is not.
   *
   * Cross-model review found both halves of this: the first draft tested `ratingNote !== ''`, which
   * missed a changed rating with no note AND reported an already-saved note as unsaved work.
   */
  const ratingWorkPending = ratingBaseline
    ? ratingDirty
    : Object.keys(ratings).length > 0 || ratingNote.trim() !== '';
  useUnsavedWorkProbe(
    changed
      || comment.trim() !== ''
      || cancelNote.trim() !== ''
      || cancelReason !== ''
      || ratingWorkPending
      || busy
      || ratingBusy,
  );
  const ratingIsStale = Boolean(
    ratingBaseline && draftPatch && hashPatch(draftPatch) !== ratingBaseline.ratedPatchHash,
  );

  if (loading) {
    return <ReviewState message="Loading lesson draft…" />;
  }

  if (notFound) {
    return (
      <ReviewState
        backPath={backPath}
        message="This work item no longer exists. Preview imports are wiped whenever the local stack is reseeded (e.g. by an e2e run) — re-run preview-lesson-pack.mjs on your pack and open the fresh URL it prints."
        tone="error"
      />
    );
  }

  if (!item || !draftPatch) {
    return <ReviewState backPath={backPath} message={error || 'Unable to load this lesson draft.'} tone="error" />;
  }

  /**
   * HARD STOP when the link's course and the item's course disagree — never a silent redirect.
   * See `lib/courseMismatch.ts`: the approve request carries no course, and the server's course
   * check proves only that this reviewer MAY touch the item's own course. Placed above every
   * decision control, and above the item render, so no approve button is ever live under a heading
   * naming a different course.
   */
  const courseMismatch = detectCourseMismatch(displayedCourseKey, item?.courseKey);
  if (courseMismatch) {
    return <ReviewState backPath={backPath} message={courseMismatchMessage(courseMismatch)} tone="error" />;
  }
  /**
   * The SAME hard stop for a link that named no course at all. An absence is not agreement: with no
   * `?courseKey` there is nothing for `detectCourseMismatch` to compare, so this screen used to
   * render the item and its decision controls while the shell header named whichever course the
   * application had fallen back to. The exit is a link that declares the item's own course, taken by
   * the reviewer — never filled in on his behalf, because a self-comparison passes always.
   */
  const undeclaredCourse = detectUndeclaredCourse(displayedCourseKey, item?.courseKey);
  if (undeclaredCourse) {
    const openHere = `${location.pathname}?${new URLSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      courseKey: undeclaredCourse.itemCourseKey,
    }).toString()}`;
    return (
      <ReviewState
        actionHref={openHere}
        actionLabel={`Open this item in course ${undeclaredCourse.itemCourseKey}`}
        backPath={backPath}
        message={undeclaredCourseMessage(undeclaredCourse)}
        tone="error"
      />
    );
  }

  const steps = recordArray(draftPatch.steps);
  const responses = recordArray(draftPatch.responses);
  const responseGroups = groupResponsesByStep(steps, responses);
  const itemId = item.workItemId;

  async function runAction(action: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await action();
      void navigate(backPath);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to update this work item.');
    } finally {
      setBusy(false);
    }
  }

  async function approveDraft(forceWithEdit = false, acknowledgeRenderIssues = false): Promise<unknown> {
    if (!item || !draftPatch) throw new Error('No lesson draft is loaded.');
    if (lessonPreviewBlocked) throw new Error('Lesson preview must be checked and ready before approval.');
    if (isNoSecondTry(item)) {
      if (forceWithEdit) throw new Error('A fresh cleared item is required for edits.');
      return approveItem(item.workItemId, undefined, false, displayedCourseKey);
    }
    try {
      if (forceWithEdit || changed || item.reviewerPatch) {
        return acknowledgeRenderIssues
          ? await approveItemWithEdit(item.workItemId, draftPatch, undefined, true, displayedCourseKey)
          : await approveItemWithEdit(item.workItemId, draftPatch, undefined, false, displayedCourseKey);
      }
      return acknowledgeRenderIssues
        ? await approveItem(item.workItemId, undefined, true, displayedCourseKey)
        : await approveItem(item.workItemId, undefined, false, displayedCourseKey);
    } catch (reason) {
      if (!isNoSecondTry(item) && !acknowledgeRenderIssues && isRenderSafetyConflict(reason)) {
        const issueCount = renderConflictIssueCount(reason);
        const confirmed = window.confirm(
          `${issueCount} render safety ${issueCount === 1 ? 'issue requires' : 'issues require'} acknowledgement. Approve anyway?`,
        );
        if (confirmed) return approveDraft(forceWithEdit, true);
      }
      throw reason;
    }
  }

  async function postComment() {
    const text = comment.trim();
    if (!text || busy) return;
    setBusy(true);
    setError('');
    setCommentStatus(null);
    try {
      const result = await commentItem(itemId, text, displayedCourseKey);
      setComment('');
      setCommentStatus(describeCommentDelivery(result.postedToSlack));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to post the comment.');
    } finally {
      setBusy(false);
    }
  }

  async function submitRating() {
    if (ratingBusy || Object.keys(ratings).length === 0) return;
    setRatingBusy(true);
    setError('');
    try {
      const note = ratingNote.trim();
      const updated = await rateItem(itemId, ratings, note || undefined);
      setItem(updated);
      if (updated.latestRatings) {
        setRatings({ ...updated.latestRatings.ratings });
        setRatingNote(updated.latestRatings.note ?? '');
        setRatingBaseline(ratingBaselineFromSnapshot(updated.latestRatings));
        setRatingStatus(`Rated by ${updated.latestRatings.ratedBy} · just now`);
        setRatingDetailsOpen(false);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to submit this rating.');
    } finally {
      setRatingBusy(false);
    }
  }

  function updatePatch(key: string, value: unknown) {
    updateDraftFromDrawer((current) => ({ ...current, [key]: value }));
  }

  function updateStep(index: number, prompt: string) {
    updateDraftFromDrawer((current) => {
      const nextSteps = recordArray(current.steps).map((step, stepIndex) =>
        stepIndex === index ? { ...step, prompt } : step,
      );
      return { ...current, steps: nextSteps };
    });
  }

  function updateResponse(index: number, field: 'value' | 'feedback' | 'is_correct', value: unknown) {
    updateDraftFromDrawer((current) => {
      const nextResponses = recordArray(current.responses).map((response, responseIndex) =>
        responseIndex === index ? { ...response, [field]: value } : response,
      );
      return { ...current, responses: nextResponses };
    });
  }

  function updateDraftFromDrawer(
    update: (current: Record<string, unknown>) => Record<string, unknown>,
  ) {
    draftChangeOriginRef.current = 'drawer';
    setDraftPatch((current) => {
      if (!current) return current;
      const next = update(current);
      draftPatchRef.current = next;
      return next;
    });
  }

  function jumpToSourceMatch(index: number) {
    const match = sourceSearchResult?.matches[index];
    if (!match) return;
    setSourceResultIndex(index);
    if (activeSource) setSourcePage({ path: activeSource.path, page: match.page });
  }

  function handleSourceSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' || !sourceSearchResult?.matches.length) return;
    event.preventDefault();
    jumpToSourceMatch((sourceResultIndex + 1) % sourceSearchResult.matches.length);
  }

  return (
    <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background-light">
      <header className="shrink-0 border-b border-slate-200 bg-white px-6 py-2.5">
        <div className="flex flex-wrap items-center gap-3">
          <Link className={`mr-1 flex items-center gap-1 text-sm font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`} to={backPath}>
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Back to queue
          </Link>
          <h1 className="min-w-0 flex-1 text-xl font-bold text-slate-900">{isNoSecondTry(item) ? 'One attempt review' : stringValue(draftPatch.title)}</h1>
          <span className={statusBadgeClass(item.status)}>{reviewStatusLabel(item.status)}</span>
          <span className="rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[10px] text-slate-500">
            {item.clusterKey}
          </span>
        </div>
        {isLessonFill(item)
          ? <LessonFillBanner key={item.workItemId} workItemId={item.workItemId} onApprovalReady={onLessonPreviewReady} />
          : item.op === 'create' && item.lessonPlacement
            ? <LessonPlacementBanner key={item.workItemId} workItemId={item.workItemId} onApprovalReady={onLessonPreviewReady} />
            : null}
        {reviewerBrief ? (
          <div className="mt-2 max-w-5xl text-sm text-slate-700">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="leading-snug">{reviewerBrief.learnerOutcome}</p>
              <p className="text-xs font-semibold text-slate-500">
                {reviewerBrief.lessonShape}
                {reviewerBrief.estimatedMinutes != null ? ` · Estimated ${reviewerBrief.estimatedMinutes} min` : ''}
              </p>
            </div>
            <details className="mt-1 text-xs text-slate-600">
              <summary className="cursor-pointer font-bold text-slate-700">Technical details</summary>
              <div className="mt-2 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
                <h2 className="font-bold uppercase text-slate-500">Rationale</h2>
                <p className="mt-1 whitespace-pre-wrap leading-relaxed">{item.rationale}</p>
              </div>
            </details>
          </div>
        ) : null}
        <div className="mt-2 flex min-w-0 items-center gap-2 overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
          <div aria-label="Lesson context" className="flex shrink-0 items-center gap-2">
            <ContextGroup
              emptyLabel="No prerequisite links proposed"
              label="Prerequisites"
              nodes={lessonContext.prerequisites}
            />
            <span aria-hidden="true" className="material-symbols-outlined shrink-0 text-[18px] text-slate-400">arrow_forward</span>
            <div className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-center text-blue-900">
              <div className="font-bold">{lessonContext.lesson.title}</div>
              {lessonContext.lesson.group ? (
                <div className={lessonContext.lesson.group.resolved ? 'mt-0.5 text-[10px] text-blue-700' : 'mt-0.5 text-[10px] text-slate-500'}>
                  Group: {lessonContext.lesson.group.label}
                </div>
              ) : null}
            </div>
            <span aria-hidden="true" className="material-symbols-outlined shrink-0 text-[18px] text-slate-400">arrow_forward</span>
            <ContextGroup
              emptyLabel={item.targetType === 'entity' && item.targetId != null ? 'No direct dependents' : 'New lesson — no dependents yet'}
              label="Dependents"
              nodes={lessonContext.dependents}
            />
          </div>
          <span aria-hidden="true" className="h-6 w-px shrink-0 bg-slate-300" />
          <div aria-label="Evidence sources" className="flex shrink-0 items-center gap-2">
            <span className="font-bold uppercase tracking-wide text-slate-500">Evidence</span>
            {evidence.map((ref, index) => (
              <button
                aria-pressed={selectedEvidence === index}
                className={`${selectedEvidence === index
                  ? 'rounded-full bg-primary px-3 py-1 text-xs font-bold text-white'
                  : 'rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-600 hover:bg-slate-50'} ${BUTTON_INTERACTION_STYLES}`}
                key={`${ref.path}-${index}`}
                onClick={() => setSelectedEvidence(index)}
                title={ref.fileName}
                type="button"
              >
                {ref.printedPage != null ? `Printed page ${ref.printedPage}` : ref.fileName}
              </button>
            ))}
            {evidence.length === 0 ? <span className="text-xs text-slate-400">No evidence attached</span> : null}
          </div>
        </div>
      </header>

      {error ? (
        <div className="mx-6 mt-2 shrink-0 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      ) : null}

      <ReviewColumns viewerId={viewerId} page="lesson" defaultLeftRatio={0.45} fullscreen={playerFullscreen}>
        <section className="min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {editOpen ? (
            <div className="h-full overflow-y-auto p-5">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-2" role="tablist" aria-label="Draft drawer mode">
                  {!isNoSecondTry(item) ? <button aria-selected={drawerMode === 'edit'} className={drawerMode === 'edit' ? 'rounded bg-slate-800 px-3 py-1.5 text-xs font-bold text-white' : 'rounded px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100'} onClick={() => setDrawerMode('edit')} role="tab" type="button">Edit</button> : null}
                  <button aria-selected={drawerMode === 'annotate'} className={drawerMode === 'annotate' ? 'rounded bg-amber-700 px-3 py-1.5 text-xs font-bold text-white' : 'rounded px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100'} onClick={() => setDrawerMode('annotate')} role="tab" type="button">
                    Annotate{annotations.filter((annotation) => annotation.status === 'open').length ? ` (${annotations.filter((annotation) => annotation.status === 'open').length})` : ''}
                  </button>
                </div>
                <button className={`text-xs font-bold text-slate-500 hover:text-slate-800 ${BUTTON_INTERACTION_STYLES}`} onClick={() => setEditOpen(false)} type="button">
                  Close
                </button>
              </div>
              {drawerMode === 'annotate' || isNoSecondTry(item) ? (
                <AnnotationDrawer
                  annotations={annotations}
                  dirty={changed}
                  onAnnotationsChange={setAnnotations}
                  onDiscard={() => {
                    if (!originalPatch) return;
                    draftChangeOriginRef.current = 'drawer';
                    draftPatchRef.current = originalPatch;
                    setDraftPatch(originalPatch);
                    setPreviewPatch(originalPatch);
                  }}
                  onError={setError}
                  patch={originalPatch ?? draftPatch}
                  readOnly={item.status !== 'pending'}
                  workItemId={itemId}
                />
              ) : <div className="flex flex-col gap-4">
                <label className="text-xs font-bold uppercase text-slate-500">
                  Title {annotationCount(annotations, { scope: 'entity', field: 'title' }) ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-800">{annotationCount(annotations, { scope: 'entity', field: 'title' })}</span> : null}
                  <input
                    aria-label="Draft title"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case"
                    onChange={(event) => updatePatch('title', event.target.value)}
                    value={stringValue(draftPatch.title)}
                  />
                </label>
                <label className="text-xs font-bold uppercase text-slate-500">
                  Summary {annotationCount(annotations, { scope: 'entity', field: 'summary' }) ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-800">{annotationCount(annotations, { scope: 'entity', field: 'summary' })}</span> : null}
                  <textarea
                    aria-label="Draft summary"
                    className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm font-normal normal-case"
                    onChange={(event) => updatePatch('summary', event.target.value)}
                    rows={3}
                    value={stringValue(draftPatch.summary)}
                  />
                </label>
                {steps.map((step, stepIndex) => (
                  <div className="rounded-lg border border-slate-200 p-4" key={`step-${recordId(step.id, stepIndex)}`}>
                    <div className="mb-2 flex items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-800">Step {stepIndex + 1}</h3>
                      {step.id !== undefined && annotationCount(annotations, { scope: 'step', id: step.id as string | number, field: 'prompt' }) ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">{annotationCount(annotations, { scope: 'step', id: step.id as string | number, field: 'prompt' })}</span> : null}
                      {step.is_question ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Question</span>
                      ) : null}
                    </div>
                    <textarea
                      aria-label={`Step ${stepIndex + 1} prompt`}
                      className="w-full rounded-lg border border-slate-300 p-3 text-sm"
                      onChange={(event) => updateStep(stepIndex, event.target.value)}
                      rows={4}
                      value={stringValue(step.prompt)}
                    />
                    {responseGroups[stepIndex]?.map(({ response, index: responseIndex }, responsePosition) => (
                      <ResponseFields
                        key={`response-${recordId(response.id, responseIndex)}`}
                        label={`Response ${responsePosition + 1}`}
                        onChange={(field, value) => updateResponse(responseIndex, field, value)}
                        response={response}
                        valueCount={response.id !== undefined ? annotationCount(annotations, { scope: 'response', id: response.id as string | number, field: 'value' }) : 0}
                        feedbackCount={response.id !== undefined ? annotationCount(annotations, { scope: 'response', id: response.id as string | number, field: 'feedback' }) : 0}
                      />
                    ))}
                  </div>
                ))}
                {responseGroups[steps.length]?.length ? (
                  <div className="rounded-lg border border-slate-200 p-4">
                    <h3 className="mb-2 text-sm font-bold text-slate-800">{steps.length ? 'Other responses' : 'Responses'}</h3>
                    {responseGroups[steps.length]!.map(({ response, index: responseIndex }, responsePosition) => (
                      <ResponseFields
                        key={`response-${recordId(response.id, responseIndex)}`}
                        label={`Response ${responsePosition + 1}`}
                        onChange={(field, value) => updateResponse(responseIndex, field, value)}
                        response={response}
                        valueCount={response.id !== undefined ? annotationCount(annotations, { scope: 'response', id: response.id as string | number, field: 'value' }) : 0}
                        feedbackCount={response.id !== undefined ? annotationCount(annotations, { scope: 'response', id: response.id as string | number, field: 'feedback' }) : 0}
                      />
                    ))}
                  </div>
                ) : null}
              </div>}
            </div>
          ) : activeSource ? (
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
                <span className="truncate text-xs font-bold text-slate-600">{activeSource.fileName}</span>
                <a className={`shrink-0 text-xs font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`} href={activeSourceHref} rel="noopener noreferrer" target="_blank">
                  Open in new tab ↗
                </a>
              </div>
              <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span aria-hidden="true" className="material-symbols-outlined text-[18px] text-slate-400">search</span>
                  <input
                    aria-label="Search source PDF"
                    className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:border-primary focus:ring-primary"
                    maxLength={200}
                    onChange={(event) => setSourceSearchQuery(event.target.value)}
                    onKeyDown={handleSourceSearchKeyDown}
                    placeholder="Search this PDF"
                    type="search"
                    value={sourceSearchQuery}
                  />
                  <span aria-live="polite" className="shrink-0 text-[11px] font-semibold text-slate-500">
                    {sourceSearchBusy
                      ? 'Searching…'
                      : sourceSearchResult?.status === 'ok'
                        ? `${sourceSearchResult.matches.length} ${sourceSearchResult.matches.length === 1 ? 'match' : 'matches'}`
                        : ''}
                  </span>
                </div>
                {sourceSearchResult?.status === 'image-only' ? (
                  <p className="mt-1.5 text-xs font-semibold text-amber-700">Search unavailable for this document (image-only PDF)</p>
                ) : sourceSearchResult?.status === 'unavailable' ? (
                  <p className="mt-1.5 text-xs font-semibold text-slate-600">Search unavailable for this document.</p>
                ) : sourceSearchError ? (
                  <p className="mt-1.5 text-xs font-semibold text-red-700">{sourceSearchError}</p>
                ) : null}
                {sourceSearchResult?.status === 'ok' && sourceSearchResult.matches.length ? (
                  <div aria-label="Source search results" className="mt-2 max-h-32 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm">
                    {sourceSearchResult.matches.map((match, index) => (
                      <button
                        aria-current={sourceResultIndex === index ? 'true' : undefined}
                        aria-label={`Page ${match.page}: ${match.snippet}`}
                        className={`block w-full border-b border-slate-100 px-3 py-2 text-left text-xs last:border-b-0 hover:bg-blue-50 ${sourceResultIndex === index ? 'bg-blue-50 text-blue-900' : 'text-slate-700'}`}
                        key={`${match.page}-${index}-${match.snippet}`}
                        onClick={() => jumpToSourceMatch(index)}
                        type="button"
                      >
                        <span className="mr-2 font-bold text-primary">Page {match.page}</span>
                        <span>{match.snippet}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              {/* No sandbox: Chrome's built-in PDF viewer needs script execution and renders a
                  broken-document icon in sandboxed frames. Framing is already constrained
                  server-side to .pdf-suffixed requests served as application/pdf. */}
              <iframe
                className="min-h-0 flex-1 w-full bg-slate-100"
                key={`${selectedEvidence}-${activeSourceHref}`}
                src={activeSourceHref}
                title={`Evidence: ${activeSource.fileName}`}
              />
            </div>
          ) : (
            <div className="grid h-full place-items-center text-sm text-slate-400">No PDF evidence attached.</div>
          )}
        </section>

        {isNoSecondTry(item) ? <NoSecondTryPreview item={item} onApprovalReady={onLessonPreviewReady} /> : (
        <section
          aria-label="Lesson player"
          className={playerFullscreen
            ? 'fixed inset-0 z-50 flex min-h-0 flex-col overflow-hidden bg-white shadow-sm'
            : 'flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm'}
        >
          <DeicticSafetyFlag item={item} changed={changed} />
          {item.renderSafety?.issues.length ? (
            <details className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
              <summary className="cursor-pointer font-bold">
                Render safety warning · {item.renderSafety.issues.length} {item.renderSafety.issues.length === 1 ? 'issue' : 'issues'}
                {changed ? ' · stale — recheck at approval' : ''}
              </summary>
              <ul className="mt-2 space-y-1 pl-5">
                {item.renderSafety.issues.map((issue, index) => (
                  <li className="list-disc" key={`${issue.fieldPath}-${issue.reason}-${index}`}>
                    <span className="font-semibold">{issue.fieldPath}</span>
                    {issue.stepId == null ? '' : ` · step ${issue.stepId}`}
                    {` · ${issue.reason}: ${issue.sample}`}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <div className={`flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 py-2 ${playerFullscreen ? 'px-6' : 'px-4'}`}>
            {playerFullscreen ? (
              <div className="flex min-w-0 items-baseline gap-x-3">
                <h2 className="truncate text-lg font-bold leading-tight text-slate-900">{stringValue(draftPatch.title)}</h2>
                {reviewerBrief ? (
                  <span className="hidden shrink-0 text-xs font-semibold text-slate-500 md:inline">
                    {reviewerBrief.lessonShape}
                    {reviewerBrief.estimatedMinutes != null ? ` · Estimated ${reviewerBrief.estimatedMinutes} min` : ''}
                  </span>
                ) : null}
              </div>
            ) : (
              <span className="truncate text-xs text-slate-500">
                {previewMode === 'editable'
                  ? 'Click text inside the player to edit it in place.'
                  : 'Learner preview — behaves like the production player; editing is off.'}
              </span>
            )}
            <div className="flex shrink-0 items-center gap-2">
              <div aria-label="Player mode" className="flex overflow-hidden rounded-lg border border-slate-300" role="group">
                <button
                  aria-pressed={previewMode === 'editable'}
                  className={`${previewMode === 'editable'
                    ? 'bg-slate-800 px-3 py-1.5 text-xs font-bold text-white'
                    : 'bg-white px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100'} ${BUTTON_INTERACTION_STYLES}`}
                  onClick={() => setPreviewMode('editable')}
                  type="button"
                >
                  Review &amp; edit
                </button>
                <button
                  aria-pressed={previewMode === 'vanilla'}
                  className={`${previewMode === 'vanilla'
                    ? 'bg-slate-800 px-3 py-1.5 text-xs font-bold text-white'
                    : 'border-l border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100'} ${BUTTON_INTERACTION_STYLES}`}
                  onClick={() => setPreviewMode('vanilla')}
                  type="button"
                >
                  Learner preview
                </button>
              </div>
              <button
                aria-label={playerFullscreen ? 'Exit full screen' : 'Enter full screen'}
                className={`grid size-8 place-items-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 ${BUTTON_INTERACTION_STYLES}`}
                onClick={() => setPlayerFullscreen((fullscreen) => !fullscreen)}
                title={playerFullscreen ? 'Exit full screen' : 'Enter full screen'}
                type="button"
              >
                <span aria-hidden="true" className="material-symbols-outlined text-[20px]">
                  {playerFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                </span>
              </button>
            </div>
          </div>
          <div className="relative min-h-0 flex-1">
            {!isLessonDraft(item) ? (
              <div className="absolute left-4 top-4 z-10 rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
                This draft has no lesson steps.
              </div>
            ) : null}
            {/* PlayerPreview keeps the graph below the item at every mount width. */}
            <div className={`absolute inset-0 w-full ${previewMode === 'editable' ? 'overflow-y-auto' : 'overflow-hidden'}`} data-testid="lesson-player-column">
              <PlayerPreview
                expandWorkflow
                entity={entity}
                mode={previewMode}
                reserveAiPaneWidth={false}
                {...(previewMode === 'editable' ? { saveNamespace: DRAFT_CAPTURE_NAMESPACE } : {})}
              />
            </div>
          </div>
        </section>
        )}
      </ReviewColumns>

      <footer className="shrink-0 border-t border-slate-200 bg-white px-6 py-2">
        {cancelOpen ? (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
            <span className="mr-1 text-xs font-bold uppercase text-slate-500">Why reject this draft?</span>
            {rejectionReasons.map((reason) => (
              <button
                aria-pressed={cancelReason === reason.label}
                className={`${cancelReason === reason.label
                  ? 'rounded-full bg-rose-600 px-2.5 py-1 text-[11px] font-bold text-white'
                  : 'rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50'} ${BUTTON_INTERACTION_STYLES}`}
                key={reason.label}
                onClick={() => setCancelReason((current) => (current === reason.label ? '' : reason.label))}
                title={reason.hint}
                type="button"
              >
                {reason.label}
              </button>
            ))}
            {/* Own line (`basis-full`) for the same reason as the practice-item screen: a taller
                box sharing this wrapping row would drag the confirm/back buttons down with it.
                AD intake 2026-08-06, `IN-20260806-2`. */}
            <textarea
              aria-label="Rejection note"
              aria-required={rejectionNoteMandatory}
              className="basis-full rounded border border-slate-300 p-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              maxLength={4000}
              onChange={(event) => setCancelNote(event.target.value)}
              placeholder={rejectionNoteMandatory ? 'Required — say what is wrong…' : 'Optional note…'}
              rows={3}
              value={cancelNote}
            />
            <button
              className={`rounded bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700 ${BUTTON_INTERACTION_STYLES}`}
              disabled={busy || !canConfirmRejection(rejectionReasons, cancelReason, cancelNote)}
              onClick={() => void runAction(() => cancelItem(item.workItemId, buildCancelNotes(cancelReason, cancelNote), displayedCourseKey))}
              type="button"
            >
              Confirm rejection
            </button>
            <button className={`px-2 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-800 ${BUTTON_INTERACTION_STYLES}`} onClick={() => setCancelOpen(false)} type="button">Back</button>
            {/* See PracticeItemReview: a `title`-only hint reaches neither keyboard nor screen-reader
                users, and `aria-live` avoids a second `role="status"` on a surface that has one. */}
            <p aria-live="polite" className="basis-full text-[11px] leading-relaxed text-slate-600" data-testid="rejection-reason-hint">
              {findRejectionReason(rejectionReasons, cancelReason)?.hint ?? ''}
            </p>
          </div>
        ) : null}
        <div className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
          {canApprove && !isNoSecondTry(item) ? (
            <button aria-pressed={editOpen && drawerMode === 'edit'} className={`shrink-0 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 ${BUTTON_INTERACTION_STYLES}`} onClick={() => { setDrawerMode('edit'); setEditOpen(true); }} type="button">
              Edit draft
            </button>
          ) : null}
          {item.status !== 'executing' && (item.status === 'pending' || annotations.some((annotation) => annotation.status === 'open')) ? (
            <button aria-pressed={editOpen && drawerMode === 'annotate'} className={`shrink-0 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100 ${BUTTON_INTERACTION_STYLES}`} onClick={() => { setDrawerMode('annotate'); setEditOpen(true); }} type="button">
              Annotate{annotations.filter((annotation) => annotation.status === 'open').length ? ` (${annotations.filter((annotation) => annotation.status === 'open').length})` : ''}
            </button>
          ) : null}
          {canApprove ? (
            <button
              className={`shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold hover:bg-slate-800 ${PRIMARY_ACTION_TEXT_STYLES} ${BUTTON_INTERACTION_STYLES}`}
              disabled={busy || lessonPreviewBlocked || approvalBlockedByRatings}
              onClick={() => void runAction(() => approveDraft())}
              title={approvalBlockedTitle}
              type="button"
            >
              Approve
            </button>
          ) : null}
          {canApprove && !isNoSecondTry(item) ? (
            <button
              className={`shrink-0 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100 ${BUTTON_INTERACTION_STYLES}`}
              disabled={busy || !changed || approvalBlockedByRatings || lessonPreviewBlocked}
              onClick={() => void runAction(() => approveDraft(true))}
              title={approvalBlockedTitle}
              type="button"
            >
              Save &amp; approve with edits
            </button>
          ) : null}
          {canCancel && !cancelOpen ? (
            <button className={`shrink-0 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-50 ${BUTTON_INTERACTION_STYLES}`} onClick={() => setCancelOpen(true)} type="button">
              Reject draft…
            </button>
          ) : null}
          <div aria-label="Draft rating" className="flex shrink-0 items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50/60 px-2 py-1">
            {ratingDimensions.map((dimension) => (
              <RatingDimensionInput
                dimension={dimension}
                key={dimension.key}
                onChange={(value) => setRatings((current) => ({ ...current, [dimension.key]: value }))}
                {...(ratings[dimension.key] !== undefined ? { value: ratings[dimension.key] } : {})}
              />
            ))}
            <div className="relative">
              <button
                aria-controls={ratingDetailsId}
                aria-expanded={ratingDetailsOpen}
                aria-label="Rating details and note"
                className={`grid size-6 place-items-center rounded text-indigo-700 hover:bg-indigo-100 ${BUTTON_INTERACTION_STYLES}`}
                onClick={() => setRatingDetailsOpen((open) => !open)}
                title="Rating details and note"
                type="button"
              >
                <span aria-hidden="true" className="material-symbols-outlined text-[17px]">info</span>
              </button>
              {ratingDetailsOpen ? (
                <section
                  aria-label="Rating details and note panel"
                  className="absolute bottom-[calc(100%+0.75rem)] right-0 z-40 w-[32rem] max-w-[calc(100vw-2rem)] whitespace-normal rounded-xl border border-indigo-200 bg-white p-4 shadow-xl"
                  id={ratingDetailsId}
                >
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-sm font-bold text-slate-900">Rating details</h2>
                    <span className="text-[11px] font-semibold text-slate-500">Optional · 1–5 stars</span>
                  </div>
                  <dl className="mt-3 space-y-2">
                    {ratingDimensions.map((dimension) => (
                      <div key={dimension.key}>
                        <dt className="text-xs font-bold text-slate-700">{dimension.label}</dt>
                        <dd className="mt-0.5 text-xs leading-relaxed text-slate-600">{dimension.description}</dd>
                      </div>
                    ))}
                  </dl>
                  <label className="mt-3 block text-xs font-bold text-slate-700">
                    Optional note
                    <textarea
                      className="mt-1 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal"
                      maxLength={4000}
                      onChange={(event) => setRatingNote(event.target.value)}
                      placeholder="Add a short note…"
                      rows={3}
                      value={ratingNote}
                    />
                  </label>
                  {historicalRatingEntries.length > 0 ? (
                    <div aria-label="Historical rating dimensions" className="mt-3 flex flex-wrap gap-1.5">
                      {historicalRatingEntries.map((entry) => (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600" key={entry.key}>
                          {entry.label} {entry.value}/5 · historical
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {ratingStatus ? (
                    <p aria-live="polite" className="mt-3 text-xs font-semibold text-emerald-700">
                      {ratingStatus}
                      {ratingIsStale ? <span className="ml-1 font-normal text-slate-500">(rated an earlier version of this draft)</span> : null}
                    </p>
                  ) : null}
                </section>
              ) : null}
            </div>
            {ratingSaved ? (
              <div aria-label="Rating saved status" className="flex shrink-0 items-center gap-1.5 text-[11px] font-bold text-emerald-700">
                <span>Rating saved ✓</span>
                {ratingIsStale ? (
                  <span className="font-normal text-amber-700">rated an earlier version of this draft</span>
                ) : null}
              </div>
            ) : (
              <button
                className={`shrink-0 rounded bg-indigo-700 px-2 py-1.5 text-[11px] font-bold text-white hover:bg-indigo-800 ${BUTTON_INTERACTION_STYLES}`}
                disabled={ratingBusy || Object.keys(ratings).length === 0}
                onClick={() => void submitRating()}
                type="button"
              >
                {ratingBusy ? 'Submitting…' : ratingBaseline ? 'Save changes' : 'Submit rating'}
              </button>
            )}
          </div>
          {/* `items-start` keeps Post comment level with the top of a box the reviewer has dragged
              taller; the `max-w-lg` cap is dropped so a wide display gives the box the width it
              has. AD intake 2026-08-06, `IN-20260806-2`. */}
          <div className="ml-auto flex min-w-0 flex-1 items-start justify-end gap-1.5">
            <textarea
              aria-label="Comment"
              className="min-w-0 flex-1 rounded-lg border border-slate-300 p-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              maxLength={4000}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Add a comment — posts to Slack…"
              rows={3}
              value={comment}
            />
            <button className={`shrink-0 rounded-lg bg-slate-800 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-slate-900 ${BUTTON_INTERACTION_STYLES}`} disabled={busy || !comment.trim()} onClick={() => void postComment()} type="button">
              Post comment
            </button>
          </div>
        </div>
        {/* Always mounted — see PracticeItemReview: a live region created together with its
            text is not reliably announced. */}
        <p
          className={commentStatus
            ? `mt-2 text-right text-xs font-medium ${COMMENT_STATUS_STYLES[commentStatus.tone]}`
            : 'sr-only'}
          role="status"
        >
          {commentStatus?.message ?? ''}
        </p>
      </footer>
    </main>
  );
}

function RatingDimensionInput({
  dimension,
  onChange,
  value,
}: {
  dimension: RatingDimension;
  onChange: (value: number) => void;
  value?: number;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <span className="text-[11px] font-bold text-slate-700">{dimension.label}</span>
      <StarRating dimension={dimension} onChange={onChange} {...(value !== undefined ? { value } : {})} />
    </div>
  );
}

function StarRating({
  dimension,
  onChange,
  value,
}: {
  dimension: RatingDimension;
  onChange: (value: number) => void;
  value?: number;
}) {
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, current: number) {
    let next: number | undefined;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = current === 5 ? 1 : current + 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = current === 1 ? 5 : current - 1;
    if (event.key === 'Home') next = 1;
    if (event.key === 'End') next = 5;
    if (next === undefined) return;
    event.preventDefault();
    onChange(next);
    buttons.current[next - 1]?.focus();
  }

  return (
    <div aria-label={dimension.label} className="flex items-center" role="radiogroup">
      {[1, 2, 3, 4, 5].map((rating) => (
        <button
          aria-checked={value === rating}
          aria-label={`${dimension.label}: ${rating} of 5`}
          className={`rounded-sm px-px text-base leading-none text-amber-500 hover:scale-110 ${BUTTON_INTERACTION_STYLES}`}
          key={rating}
          onClick={() => onChange(rating)}
          onKeyDown={(event) => handleKeyDown(event, rating)}
          ref={(node) => {
            buttons.current[rating - 1] = node;
          }}
          role="radio"
          tabIndex={value === rating || (value === undefined && rating === 1) ? 0 : -1}
          type="button"
        >
          {value !== undefined && rating <= value ? '★' : '☆'}
        </button>
      ))}
    </div>
  );
}

function ContextGroup({
  emptyLabel,
  label,
  nodes,
}: {
  emptyLabel: string;
  label: string;
  nodes: LessonContextNode[];
}) {
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <span className="font-bold uppercase tracking-wide text-slate-500">{label}</span>
      {nodes.length > 0 ? nodes.map((node) => (
        <span
          className={node.resolved
            ? 'rounded-full border border-slate-200 bg-white px-2 py-1 font-semibold text-slate-700'
            : 'rounded-full border border-dashed border-slate-300 bg-white px-2 py-1 font-mono text-slate-500'}
          key={node.id}
          title={node.resolved ? node.id : `Unresolved id ${node.id}`}
        >
          {node.label}
        </span>
      )) : <span className="text-slate-500">{emptyLabel}</span>}
    </div>
  );
}

function ResponseFields({
  response,
  label,
  onChange,
  valueCount,
  feedbackCount,
}: {
  response: Record<string, unknown>;
  label: string;
  onChange: (field: 'value' | 'feedback' | 'is_correct', value: unknown) => void;
  valueCount: number;
  feedbackCount: number;
}) {
  return (
    <fieldset className="mt-3 border-t border-slate-100 pt-3">
      <legend className="px-1 text-xs font-bold text-slate-500">{label}</legend>
      <label className="mt-2 block text-xs font-bold text-slate-500">
        Value {valueCount ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-800">{valueCount}</span> : null}
        <input aria-label={`${label} value`} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-normal" onChange={(event) => onChange('value', event.target.value)} value={stringValue(response.value)} />
      </label>
      <label className="mt-2 block text-xs font-bold text-slate-500">
        Feedback {feedbackCount ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-800">{feedbackCount}</span> : null}
        <textarea aria-label={`${label} feedback`} className="mt-1 w-full rounded border border-slate-300 p-2 text-sm font-normal" onChange={(event) => onChange('feedback', event.target.value)} rows={2} value={stringValue(response.feedback)} />
      </label>
      <label className="mt-2 flex items-center gap-2 text-xs font-bold text-slate-600">
        <input checked={response.is_correct === true} onChange={(event) => onChange('is_correct', event.target.checked)} type="checkbox" />
        Correct answer
      </label>
    </fieldset>
  );
}

function groupResponsesByStep(steps: Record<string, unknown>[], responses: Record<string, unknown>[]) {
  const grouped: Array<Array<{ response: Record<string, unknown>; index: number }>> = steps.map(() => []);
  const unassigned: Array<{ response: Record<string, unknown>; index: number }> = [];
  responses.forEach((response, responseIndex) => {
    const id = String(response.id);
    const stepIndex = steps.findIndex((step) => stepResponseIds(step).has(id));
    const entry = { response, index: responseIndex };
    if (stepIndex >= 0) grouped[stepIndex]!.push(entry);
    else unassigned.push(entry);
  });
  grouped.push(unassigned);
  return grouped;
}

function stepResponseIds(step: Record<string, unknown>): Set<string> {
  const ids = new Set<string>();
  if (Array.isArray(step.response_ids)) {
    step.response_ids.forEach((id) => ids.add(String(id)));
  }
  if (Array.isArray(step.response_groups)) {
    step.response_groups.forEach((group) => {
      if (group && typeof group === 'object' && Array.isArray((group as Record<string, unknown>).response_ids)) {
        ((group as Record<string, unknown>).response_ids as unknown[]).forEach((id) => ids.add(String(id)));
      }
    });
  }
  return ids;
}

function recordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry))
    : [];
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function ratingBaselineFromSnapshot(snapshot: RatingSnapshot): RatingBaseline {
  return {
    ratings: { ...snapshot.ratings },
    trimmedNote: snapshot.note?.trim() ?? '',
    ratedPatchHash: snapshot.patchHash,
  };
}

function ratingsEqual(left: Record<string, number>, right: Record<string, number>): boolean {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return leftKeys.length === rightKeys.length &&
    leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key]);
}

function reviewStatusLabel(status: WorkItemSummary['status']): string {
  return status === 'cancelled' ? 'rejected' : status.replaceAll('_', ' ');
}

function formatRatingAge(value: string): string {
  const elapsed = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 60_000) return 'just now';
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(value).toLocaleDateString();
}

function recordId(value: unknown, fallback: number): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : String(fallback);
}

function isRenderSafetyConflict(reason: unknown): reason is ApiRequestError & { details: Record<string, unknown> } {
  return reason instanceof ApiRequestError &&
    reason.status === 409 &&
    Boolean(reason.details) &&
    typeof reason.details === 'object' &&
    (reason.details as Record<string, unknown>).code === 'RENDER_SAFETY_ISSUES';
}

function renderConflictIssueCount(reason: ApiRequestError & { details: Record<string, unknown> }): number {
  const report = reason.details.renderSafety;
  if (!report || typeof report !== 'object') return 1;
  const issues = (report as Record<string, unknown>).issues;
  return Array.isArray(issues) && issues.length > 0 ? issues.length : 1;
}

function ReviewState({ message, tone = 'neutral', backPath, actionHref, actionLabel }: {
  message: string;
  tone?: 'neutral' | 'error';
  backPath?: string;
  /** A way FORWARD out of a refusal. Used by the undeclared-course stop, whose whole point is that
   *  the reviewer — not the code — states which course he is looking at. */
  actionHref?: string;
  actionLabel?: string;
}) {
  return (
    <main className="grid flex-1 place-items-center bg-background-light p-6">
      <div className={tone === 'error' ? 'rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800' : 'text-sm text-slate-500'}>
        <p>{message}</p>
        <div className="flex items-center gap-4">
          {actionHref && actionLabel ? (
            <Link
              className={`mt-3 inline-block text-sm font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`}
              data-testid="declare-course-action"
              to={actionHref}
            >
              {actionLabel}
            </Link>
          ) : null}
          {backPath ? <Link className={`mt-3 inline-block text-sm font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`} to={backPath}>← Back to queue</Link> : null}
        </div>
      </div>
    </main>
  );
}
